import type Opportunity from '@/types/serializers/Opportunity';
import { Calendar } from 'lucide-react';
import { cva } from 'class-variance-authority';
import { Card, CardContent, CardHeader } from "@/components/ui/card.tsx";

interface OpportunityCardProps {
    opportunity: Opportunity;
    onClick?: (opportunityId: number) => void;
}

const priorityBadge = cva(
    'inline-flex px-2 py-1 text-xs font-medium rounded-full',
    {
        variants: {
            priority: {
                critical: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
                high: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
                medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
                low: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
            },
        },
        defaultVariants: {
            priority: 'Medium',
        },
    }
);

const probabilityText = cva(
    'text-sm font-extrabold',
    {
        variants: {
            probability: {
                high: 'text-green-600 dark:text-green-400',      // >= 80
                medium: 'text-blue-600 dark:text-blue-400',      // >= 60
                low: 'text-orange-600 dark:text-orange-400',     // >= 40
                critical: 'text-red-600 dark:text-red-400',      // < 40
            },
        },
        defaultVariants: {
            probability: 'medium',
        },
    }
);

const cardContainer = cva(
    '',
    {
        variants: {
            isNew: {
                true: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800',
            },
        }
    }
);

export default function OpportunityCard({opportunity, onClick}: OpportunityCardProps) {
    const formatCurrency = (amount: number | null) => {
        if (!amount) return 'N/A';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
        });
    };

    const getProbabilityVariant = (probability: number) => {
        if (probability >= 80) return 'high';
        if (probability >= 60) return 'medium';
        if (probability >= 40) return 'low';
        return 'critical';
    };

    const handleClick = () => {
        onClick?.(opportunity.id);
    };

    // Local Components
    const OpportunityCardHeader = () => (
        <div className="p-6 pb-4">
            <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-neutral-50 group-hover:text-accent transition-colors line-clamp-1">
                            {opportunity.title}
                        </h3>
                        {opportunity.is_new && (
                            <span
                                className="inline-flex px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300 rounded-full">
                NEW
              </span>
                        )}
                    </div>
                    <div className="flex items-center space-x-1 mb-3">
                        <Calendar className="w-3 h-3 text-slate-400"/>
                        <span className="text-xs text-slate-500 dark:text-slate-400">
              {formatDate(opportunity.last_activity)}
            </span>
                    </div>
                    <div className="flex items-center space-x-2 mb-5">
                        {opportunity.account.logo && (
                            <img src={opportunity.account.logo} alt={`${opportunity.account.name} Logo`}
                                 className="w-4 h-4"/>
                        )}
                        <span className="text-sm text-slate-600 dark:text-neutral-300">
              {opportunity.account.name}
            </span>
                    </div>
                    <div className="flex items-center space-x-3">
            <span className={priorityBadge({
                priority: opportunity.priority?.toLowerCase() as 'Critical' | 'High' | 'Medium' | 'Low'
            })}>
              {opportunity.priority}
            </span>
                    </div>
                </div>
            </div>
        </div>
    );

    const KeyMetrics = () => (
        <div className="px-6 pb-4">
            <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center">
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Deal Size</p>
                    <div className="flex items-center justify-center">
            <span className="text-sm font-extrabold text-slate-900 dark:text-neutral-50">
              {formatCurrency(opportunity.deal_value)}
            </span>
                    </div>
                </div>

                <div className="text-center">
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Validation Score</p>
                    <div className="flex items-center justify-center">
            <span className={probabilityText({
                probability: getProbabilityVariant(Number(opportunity.validation_score))
            })}>
              {opportunity.validation_score}%
            </span>
                    </div>
                </div>

            </div>
        </div>
    );

    const PainPointSolution = () => (
        <div className="space-y-3">
            <div>
                <p className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Pain Point</p>
                <p className="text-sm text-slate-600 dark:text-neutral-300 line-clamp-2">
                    {opportunity.pain_point.title}
                </p>
            </div>
            <div>
                <p className="text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Solution</p>
                <p className="text-sm text-slate-600 dark:text-neutral-300 line-clamp-2">
                    {opportunity.solution.title}
                </p>
            </div>
        </div>
    );

    return (
        <Card onClick={handleClick} className={cardContainer({isNew: opportunity.is_new})}>
            <CardHeader>
                <OpportunityCardHeader/>
            </CardHeader>
            <CardContent>
                <KeyMetrics/>
                <PainPointSolution/>
            </CardContent>
        </Card>
    );
}